

import React, { useState, useEffect } from 'react';
const API_URL = process.env.REACT_APP_API_URL;
const token = localStorage.getItem('accessToken');

export default function LossSection({ caseId, sofaResponse }) {
  const [losses, setLosses] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [newLoss, setNewLoss] = useState({
    property_loss_description: '',
    insurance_coverage_description: '',
    loss_date: '',
    property_value_lost: ''
  });
  const [editLossData, setEditLossData] = useState(null);

  useEffect(() => {
    if (!sofaResponse) return;
    async function fetchLosses() {
      try {
        const res = await fetch(
          `${API_URL}/cases/${caseId}/sofa-losses/?sofa_response=${sofaResponse.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error('Failed to fetch losses');
        setLosses(await res.json());
      } catch (err) {
        console.error(err);
      }
    }
    fetchLosses();
  }, [sofaResponse, caseId]);

  function openAddModal() {
    setNewLoss({
      property_loss_description: '',
      insurance_coverage_description: '',
      loss_date: '',
      property_value_lost: ''
    });
    setModalOpen(true);
  }

  function openEditModal(item) {
    setEditLossData({ ...item });
    setEditModalOpen(true);
  }

  async function handleSaveNew() {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-losses/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(newLoss)
        }
      );
      if (!res.ok) throw new Error('Failed to add loss');
      const created = await res.json();
      setLosses(prev => [...prev, created]);
      setModalOpen(false);
    } catch (err) {
      console.error(err);
    }
  }

  async function handleSaveEdit() {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-losses/${editLossData.id}/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(editLossData)
        }
      );
      if (!res.ok) throw new Error('Failed to update loss');
      const updated = await res.json();
      setLosses(prev => prev.map(l => l.id === updated.id ? updated : l));
      setEditModalOpen(false);
    } catch (err) {
      console.error(err);
    }
  }

  async function handleDelete(id) {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-losses/${id}/`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (!res.ok) throw new Error('Failed to delete loss');
      setLosses(prev => prev.filter(l => l.id !== id));
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <section className="p-6 bg-white shadow-lg rounded mb-6">
      <h2 className="text-xl font-semibold mb-4">Question 15: Losses</h2>
      <p className="text-sm text-gray-600 mb-4">
            List losses from the last year due to theft, fire, other disaster, or gambling.
        </p>
      {losses.length === 0 ? (
        <p>No losses recorded.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          {losses.map(item => (
            <div
              key={item.id}
              className="p-4 bg-white shadow rounded cursor-pointer relative hover:shadow-lg transition-shadow"
              onClick={() => openEditModal(item)}
            >
              <button
                onClick={e => { e.stopPropagation(); handleDelete(item.id); }}
                className="absolute top-2 right-2 text-red-600 hover:text-red-800"
              >
                {/* Trash Icon */}
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" clipRule="evenodd" d="M6 2a1 1 0 00-1 1v1H3a1 1 0 000
                    2h14a1 1 0 000-2h-2V3a1 1 0 00-1-1H6zM4
                    6v10a2 2 0 002 2h8a2 2 0 002-2V6H4zm3
                    2a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0 00-2
                    0zm4 0a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0
                    00-2 0z"/>
                </svg>
              </button>
              <p className="font-medium mb-2">{item.property_loss_description}</p>
              {item.insurance_coverage_description && (
                <p className="text-sm mb-2"><strong>Insurance:</strong> {item.insurance_coverage_description}</p>
              )}
              <p className="text-sm mb-1"><strong>Date:</strong> {item.loss_date}</p>
              <p className="text-sm"><strong>Value Lost:</strong> ${item.property_value_lost}</p>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={openAddModal}
        className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
      >
        Add Loss
      </button>

      {/* Add Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 sm:p-6 rounded shadow-lg w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add Loss</h3>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Property Loss Description</label>
              <textarea
                className="w-full border rounded px-2 py-1"
                value={newLoss.property_loss_description}
                onChange={e => setNewLoss(prev => ({ ...prev, property_loss_description: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Insurance Coverage Description</label>
              <textarea
                className="w-full border rounded px-2 py-1"
                value={newLoss.insurance_coverage_description}
                onChange={e => setNewLoss(prev => ({ ...prev, insurance_coverage_description: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Loss Date</label>
                <input
                  type="date"
                  className="w-full border rounded px-2 py-1"
                  value={newLoss.loss_date}
                  onChange={e => setNewLoss(prev => ({ ...prev, loss_date: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Value Lost</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full border rounded px-2 py-1"
                  value={newLoss.property_value_lost}
                  onChange={e => setNewLoss(prev => ({ ...prev, property_value_lost: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
              <button onClick={handleSaveNew} className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && editLossData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 sm:p-6 rounded shadow-lg w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Edit Loss</h3>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Property Loss Description</label>
              <textarea
                className="w-full border rounded px-2 py-1"
                value={editLossData.property_loss_description || ''}
                onChange={e => setEditLossData(prev => ({ ...prev, property_loss_description: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Insurance Coverage Description</label>
              <textarea
                className="w-full border rounded px-2 py-1"
                value={editLossData.insurance_coverage_description || ''}
                onChange={e => setEditLossData(prev => ({ ...prev, insurance_coverage_description: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Loss Date</label>
                <input
                  type="date"
                  className="w-full border rounded px-2 py-1"
                  value={editLossData.loss_date || ''}
                  onChange={e => setEditLossData(prev => ({ ...prev, loss_date: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Value Lost</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full border rounded px-2 py-1"
                  value={editLossData.property_value_lost || ''}
                  onChange={e => setEditLossData(prev => ({ ...prev, property_value_lost: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={() => setEditModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
              <button onClick={handleSaveEdit} className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}